import { formatNumber } from "@entrilia/shared/utilities/formatters";
import { getUserLocaleCode } from "@entrilia/shared/utilities/userLocaleConfig";
import type { CellDrillDownRef, ReportField } from "../../../../../../../../../api/types/svTypes";
import { hydrateFormulaPlaceholders } from "../../fields/custom-field/utilities/customFormulaInitialValues";

export const getFormulaPreview = (field: ReportField, allFields: ReportField[]): string | undefined => {
  const formula = field.config.formula;
  if (!formula) {
    return undefined;
  }

  const fieldsById = new Map(allFields.map((item) => [item.config.guid, item]));
  const formulaParameters = field.config.formulaParameters ?? [];
  const hydratedFormula = hydrateFormulaPlaceholders(formula, formulaParameters);

  return hydratedFormula.replace(/"([^"]+)"/g, (_, fieldGuid: string) => {
    const caption = getFieldDisplayName(fieldsById.get(fieldGuid), fieldGuid);
    return '"' + caption + '"';
  });
};

export const getFormulaCaptionsPreview = (
  formula: string | undefined,
  drillDownRefs: CellDrillDownRef[] = []
): string | undefined => {
  if (!formula) {
    return undefined;
  }

  const captionsByFieldId = new Map(drillDownRefs.map((ref) => [ref.fieldId, ref.fieldCaption]));

  const formulaWithGrandTotals = formula.replace(/GrandTotal\("([^"]+)"\)/g, (_match, fieldGuid: string) => {
    const caption = captionsByFieldId.get(fieldGuid) ?? fieldGuid;
    return `GrandTotal("${caption}")`;
  });

  return formulaWithGrandTotals.replace(/"([^"]+)"/g, (_, fieldGuid: string) => {
    const caption = captionsByFieldId.get(fieldGuid);
    return caption ? `"${caption}"` : `"${fieldGuid}"`;
  });
};

export const getFormulaValuesPreview = (
  formula: string | undefined,
  drillDownRefs: CellDrillDownRef[] = []
): string | undefined => {
  if (!formula) {
    return undefined;
  }

  const refsByKey = new Map(drillDownRefs.map((ref) => [`${ref.fieldId}|${ref.functionName ?? ""}`, ref]));

  const formulaWithGrandTotals = formula.replace(/GrandTotal\("([^"]+)"\)/g, (_match, fieldGuid: string) => {
    const ref = refsByKey.get(`${fieldGuid}|GrandTotal`);
    return formatFieldNumber(ref?.value ?? 0);
  });

  return formulaWithGrandTotals.replace(/"([^"]+)"/g, (_, fieldGuid: string) => {
    const ref = refsByKey.get(`${fieldGuid}|`);
    return formatFieldNumber(ref?.value ?? 0);
  });
};

export const getFieldDisplayName = (field: ReportField | undefined, fallback: string): string => {
  return field?.config.customLabel || field?.meta.caption || fallback;
};

export const formatFieldNumber = (value: number): string => {
  return formatNumber(value, { maximumFractionDigits: 2, minimumFractionDigits: 2 }, getUserLocaleCode());
};
